import { useState, useCallback } from 'react';
import { IAgenda, IAppointment, IPatient, IResearcher } from '../constants/types';
import { RequestStatus } from '../pages/hospital/Request/types';
import { getAppoinmentsDateService } from '../services/agenda';
import { formatBirthdateForPDF, formatTimeForPDF } from '../components/pdf/utils/dataFormatters';
import { patientFullName, getPatientID } from '../utils/index.jsx';
import { GroupedSurgeryData, SurgeryPDFRow } from '../components/pdf/documents/SurgerySchedulePDFDocument';

interface UseSurgeryWeekPDFProps {
  uuidInvestigation: string;
  agendas: IAgenda[];
  patientsPersonalData: IPatient[];
  researchers: IResearcher[];
  locale: string;
}

interface UseSurgeryWeekPDFReturn {
  loading: boolean;
  weekData: GroupedSurgeryData[];
  weekRange: { start: Date; end: Date };
  fetchWeekData: (selectedDate: Date) => Promise<boolean>;
}

/**
 * Compute Monday–Sunday range for the week containing the given date.
 */
function getWeekRange(date: Date): { start: Date; end: Date } {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return { start: monday, end: sunday };
}

/**
 * Map VPA-related RequestStatus to a short display string.
 */
export function getVPAStatus(status: number): string {
  switch (status) {
    case RequestStatus.ANESTHESIOLOGIST_REJECTED:
      return '✗';
    case RequestStatus.PENDING_ANESTHESIOLOGIST:
      return '—';
    case RequestStatus.ANESTHESIOLOGIST_ACCEPTED:
    case RequestStatus.READY_FOR_SURGERY:
    case RequestStatus.CHECKED_IN:
    case RequestStatus.PAYMENT_COMPLETED:
    case RequestStatus.COMPLETED:
    // SHER-935: the post-VPA halves are only reachable once the visit has cleared the
    // patient, so they belong here. Without them they fall to the default and the column
    // reads "—" — no visit yet — for someone who has demonstrably had one.
    case RequestStatus.PENDING_PAYMENT_AFTER_VPA:
    case RequestStatus.PAYMENT_COMPLETED_AFTER_VPA:
      // VPA was accepted if we've progressed past that stage
      return '✓';
    default:
      // Not yet reached VPA stage or unknown status
      return '—';
  }
}

/**
 * Extract researcher surname (or full name if no surname).
 */
function researcherName(researcher: IResearcher | null | undefined): string {
  if (!researcher) return '';
  const parts = [researcher.name, researcher.surnames].filter(Boolean);
  return parts.join(' ') || '';
}

export const useSurgeryWeekPDF = ({
  uuidInvestigation,
  agendas,
  patientsPersonalData,
  researchers,
  locale,
}: UseSurgeryWeekPDFProps): UseSurgeryWeekPDFReturn => {
  const [loading, setLoading] = useState(false);
  const [weekData, setWeekData] = useState<GroupedSurgeryData[]>([]);
  const [weekRange, setWeekRange] = useState<{ start: Date; end: Date }>({ start: new Date(), end: new Date() });

  const fetchWeekData = useCallback(async (selectedDate: Date) => {
    setLoading(true);
    try {
      const range = getWeekRange(selectedDate);
      setWeekRange(range);

      // Build array of 7 dates (Mon–Sun)
      const dates: Date[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(range.start);
        d.setDate(range.start.getDate() + i);
        dates.push(d);
      }

      // Fetch all appointments in parallel: each day × each agenda
      const fetchPromises: { date: Date; agenda: IAgenda; promise: Promise<{ status: number; appointments: IAppointment[] }> }[] = [];
      for (const date of dates) {
        for (const agenda of agendas) {
          fetchPromises.push({
            date,
            agenda,
            promise: getAppoinmentsDateService(uuidInvestigation, agenda.uuid, date),
          });
        }
      }

      const results = await Promise.allSettled(fetchPromises.map(fp => fp.promise));

      // Group results by date → agenda
      const grouped: GroupedSurgeryData[] = [];

      for (let i = 0; i < fetchPromises.length; i++) {
        const result = results[i];
        const { date, agenda } = fetchPromises[i];

        if (result.status !== 'fulfilled') continue;

        const appointments = result.value.appointments;
        if (!appointments || appointments.length === 0) continue;

        // Sort appointments by start time
        const sorted = [...appointments].sort((a, b) => a.startDateTime - b.startDateTime);

        const rows: SurgeryPDFRow[] = sorted.map(appointment => {
          // Join with decrypted patient data from client
          const patient = patientsPersonalData.find(p => p.uuid === appointment.patient.uuid);
          const personalData = (patient as any)?.personalData || {};

          // Resolve surgeon from researchers list
          const surgeonResearcher = appointment.mainSurgeon
            ? researchers.find(r => r.uuid === (appointment.mainSurgeon as any)?.researcher?.uuid || r.uuid === (appointment.mainSurgeon as any)?.uuid)
            : null;

          // Resolve first anesthetist from surgical team
          const anesthetistResearcher = appointment.surgicalTeam && appointment.surgicalTeam.length > 0
            ? researchers.find(r => r.uuid === (appointment.surgicalTeam![0] as any)?.researcher?.uuid || r.uuid === (appointment.surgicalTeam![0] as any)?.uuid)
            : null;

          // Extract surgery-specific fields from requestAppointment
          const reqAppt = appointment.requestAppointment as any;
          const indication = reqAppt?.typeSpecificData?.ict || reqAppt?.typeSpecificData?.surgicalIndication || '';
          const surgeryType = reqAppt?.service?.description || reqAppt?.service?.name || '';
          const vpaStatus = getVPAStatus(reqAppt?.status);

          return {
            patientName: patient ? patientFullName(personalData) : 'Unknown',
            healthId: patient && personalData ? getPatientID(patient) : '',
            dob: personalData.birthdate ? formatBirthdateForPDF(personalData.birthdate, locale) : '',
            dateTime: formatTimeForPDF(appointment.startDateTime, locale),
            indication,
            surgeryType,
            vpaStatus,
            surgeon: researcherName(surgeonResearcher),
            anesthetist: researcherName(anesthetistResearcher),
          };
        });

        grouped.push({
          date,
          agendaName: agenda.name,
          rows,
        });
      }

      // Sort by date, then by agenda name within each date
      grouped.sort((a, b) => {
        const dateDiff = a.date.getTime() - b.date.getTime();
        if (dateDiff !== 0) return dateDiff;
        return a.agendaName.localeCompare(b.agendaName);
      });

      setWeekData(grouped);
      return true;
    } catch (error) {
      console.error('Error fetching surgery week data:', error);
      setWeekData([]);
      return false;
    } finally {
      setLoading(false);
    }
  }, [uuidInvestigation, agendas, patientsPersonalData, researchers, locale]);

  return { loading, weekData, weekRange, fetchWeekData };
};
